import { memo, useState } from 'react'
import type { DailyPoint } from '../model/contracts'

const W = 600
const H = 200
const PAD = { top: 16, right: 16, bottom: 32, left: 36 }

function scaleX(i: number, total: number): number {
  if (total <= 1) return PAD.left
  return PAD.left + (i / (total - 1)) * (W - PAD.left - PAD.right)
}

function scaleY(v: number, min: number, max: number): number {
  if (max === min) return PAD.top + (H - PAD.top - PAD.bottom) / 2
  const ratio = (v - min) / (max - min)
  return H - PAD.bottom - ratio * (H - PAD.top - PAD.bottom)
}

function buildPath(points: DailyPoint[], allMin: number, allMax: number): string {
  if (points.length === 0) return ''
  return points
    .map((p, i) => {
      const x = scaleX(i, points.length)
      const y = scaleY(p.count, allMin, allMax)
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
}

function mergedDates(a: DailyPoint[], b: DailyPoint[]): string[] {
  const set = new Set([...a.map((p) => p.date), ...b.map((p) => p.date)])
  return Array.from(set).sort()
}

function aligned(series: DailyPoint[], dates: string[]): DailyPoint[] {
  const map = new Map(series.map((p) => [p.date, p.count]))
  return dates.map((date) => ({ date, count: map.get(date) ?? 0 }))
}

export const DualLineChart = memo(function DualLineChart({
  seriesA,
  seriesB,
  labelA,
  labelB,
}: {
  seriesA: DailyPoint[]
  seriesB: DailyPoint[]
  labelA: string
  labelB: string
}) {
  const [hiddenA, setHiddenA] = useState(false)
  const [hiddenB, setHiddenB] = useState(false)

  const dates = mergedDates(seriesA, seriesB)
  const alignedA = aligned(seriesA, dates)
  const alignedB = aligned(seriesB, dates)

  const allCounts = [...alignedA, ...alignedB].map((p) => p.count)
  const minVal = allCounts.length > 0 ? Math.min(...allCounts) : 0
  const maxVal = allCounts.length > 0 ? Math.max(...allCounts) : 1

  const pathA = buildPath(alignedA, minVal, maxVal)
  const pathB = buildPath(alignedB, minVal, maxVal)

  const labelStep = Math.max(1, Math.floor(dates.length / 5))

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center gap-4 text-xs">
        <button
          type="button"
          onClick={() => setHiddenA((v) => !v)}
          className="flex items-center gap-1.5 transition-opacity"
          style={{ opacity: hiddenA ? 0.4 : 1 }}
        >
          <span className="inline-block h-2 w-6 rounded-full bg-primary" />
          <span className="text-muted-foreground">{labelA}</span>
        </button>
        <button
          type="button"
          onClick={() => setHiddenB((v) => !v)}
          className="flex items-center gap-1.5 transition-opacity"
          style={{ opacity: hiddenB ? 0.4 : 1 }}
        >
          <span className="inline-block h-2 w-6 rounded-full bg-accent-foreground" />
          <span className="text-muted-foreground">{labelB}</span>
        </button>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${labelA} vs ${labelB}`}
        className="h-auto w-full"
      >
        {[0, 0.5, 1].map((t) => {
          const y = scaleY(minVal + t * (maxVal - minVal), minVal, maxVal)
          return (
            <g key={t}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.08}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 4}
                y={y + 4}
                textAnchor="end"
                fontSize={9}
                fill="currentColor"
                fillOpacity={0.4}
              >
                {Math.round(minVal + t * (maxVal - minVal))}
              </text>
            </g>
          )
        })}

        {dates
          .filter((_, i) => i % labelStep === 0)
          .map((date) => {
            const idx = dates.indexOf(date)
            const x = scaleX(idx, dates.length)
            return (
              <text
                key={date}
                x={x}
                y={H - 4}
                textAnchor="middle"
                fontSize={8}
                fill="currentColor"
                fillOpacity={0.4}
              >
                {date.slice(5)}
              </text>
            )
          })}

        {!hiddenA && pathA && (
          <path
            d={pathA}
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {!hiddenB && pathB && (
          <path
            d={pathB}
            fill="none"
            stroke="var(--color-accent-foreground)"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
      </svg>
    </div>
  )
})
